import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API from "../util/API";
import { readScopedJson, writeScopedJson } from "../util/privacyConsent";

/* =========================
   LOCAL STORAGE HELPERS
========================= */
const STORAGE_KEY = "user_settings";

const loadFromLocalStorage = () => {
  try {
    return readScopedJson(STORAGE_KEY, null, { persistWhenAccepted: true });
  } catch {
    return null;
  }
};

const saveToLocalStorage = (state) => {
  try {
    const settingsToSave = {
      theme: state.theme,
      language: state.language,
      currency: state.currency,
      shipToCountry: state.shipToCountry,
      notifications: state.notifications,
      userOverride: state.userOverride === true,
    };
    writeScopedJson(STORAGE_KEY, settingsToSave, { persistWhenAccepted: true });
  } catch {
    // Ignore storage errors silently
  }
};

const normalizeCountryCode = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  return raw || "US";
};

const normalizeCurrencyCode = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(raw) ? raw : "USD";
};

/* =========================
   ASYNC THUNKS
========================= */
export const updateUserSettings = createAsyncThunk(
  "userSettings/updateUserSettings",
  async (settings) => {
    // Example API call (optional)
    // const response = await API.post("/user/settings", settings);
    // return response.data;
    return settings;
  }
);

export const userProfileInfo = createAsyncThunk(
  "userAuth/userProfileInfo",
  async (_, thunkAPI) => {
    try {
      const res = await API.get("/user/profile");
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message);
    }
  }
);

/* =========================
   INITIAL STATE
 ========================= */
const initialState = {
  userProfile: null,

  theme: "light",
  language: "en",
  currency: "USD",
  shipToCountry: "US",
  notifications: true,
  userOverride: false,
  // Session-only geo resolution provenance ('mmdb' | 'cache' | 'default' |
  // 'fallback-disabled' | 'no-enabled' | 'lookup' | 'lookup-failed' | null).
  // Deliberately NOT persisted and NOT part of override logic.
  countrySource: null,
  detectedCountry: null,
  settingsHydrated: false,

  status: "idle",
  error: null,
};

const loadInitialState = () => {
  try {
    const savedSettings = loadFromLocalStorage();
    if (!savedSettings || typeof savedSettings !== "object") return null;
    
    return {
      theme: savedSettings.theme || "light",
      language: savedSettings.language || "en",
      currency: normalizeCurrencyCode(savedSettings.currency),
      shipToCountry: savedSettings.shipToCountry || "US",
      notifications:
        savedSettings.notifications !== undefined
          ? savedSettings.notifications
          : true,
      userOverride: savedSettings.userOverride === true,
    };
  } catch {
    return null;
  }
};

export const hydrateSettings = (state, savedData) => {
  if (!savedData || typeof savedData !== "object") return state;
  
  return {
    ...state,
    ...savedData,
    settingsHydrated: true,
  };
};

/* =========================
   SLICE
 ========================= */
const userSettingsSlice = createSlice({
  name: "userSettings",
  initialState,
  reducers: {
    hydrateFromStorage(state, action) {
      const savedData = action.payload;
      if (!savedData || typeof savedData !== "object") return state;
      
      if (savedData.theme !== undefined) state.theme = savedData.theme;
      if (savedData.language !== undefined) state.language = savedData.language;
      if (savedData.currency !== undefined) state.currency = normalizeCurrencyCode(savedData.currency);
      if (savedData.shipToCountry !== undefined) state.shipToCountry = normalizeCountryCode(savedData.shipToCountry);
      if (savedData.notifications !== undefined) state.notifications = savedData.notifications;
      if (savedData.userOverride !== undefined) state.userOverride = savedData.userOverride === true;
      
      state.settingsHydrated = true;
    },
    setTheme(state, action) {
      state.theme = action.payload;
      saveToLocalStorage(state);
    },
    setLanguage(state, action) {
      state.language = action.payload;
      saveToLocalStorage(state);
    },
    setNotifications(state, action) {
      state.notifications = action.payload;
      saveToLocalStorage(state);
    },
    setShipToCountry(state, action) {
      const nextCountry =
        typeof action.payload === "object"
          ? String(action.payload?.country || action.payload?.shipToCountry || "").trim().toUpperCase()
          : String(action.payload || "").trim().toUpperCase();
      const nextCurrency =
        typeof action.payload === "object"
          ? normalizeCurrencyCode(action.payload?.currency)
          : state.currency;
      state.shipToCountry = nextCountry;
      state.currency = nextCurrency;
      saveToLocalStorage(state);
    },

    /* 🔥 MAIN SETTINGS UPDATE (used by SettingsPopup) */
    setSettingsSlice(state, action) {
      const { theme, language, currency, shipToCountry, notifications } =
        action.payload;

      if (theme !== undefined) state.theme = theme;
      if (language !== undefined) state.language = language;
      if (currency !== undefined) state.currency = currency;
      if (shipToCountry !== undefined)
        state.shipToCountry = shipToCountry;
      if (notifications !== undefined)
        state.notifications = notifications;

      if (action.payload?.userOverride !== undefined) {
        state.userOverride = action.payload.userOverride === true;
      }

      state.shipToCountry = normalizeCountryCode(state.shipToCountry);
      state.currency = normalizeCurrencyCode(state.currency);

      saveToLocalStorage(state);
    },
    setSettingsHydrated(state, action) {
      state.settingsHydrated = action.payload === true;
    },
    setCountrySource(state, action) {
      const payload = action?.payload;
      if (payload && typeof payload === "object") {
        state.countrySource = typeof payload.source === "string" ? payload.source : null;
        state.detectedCountry =
          String(payload.detectedCountry || "").trim().toUpperCase() || null;
      } else {
        state.countrySource = typeof payload === "string" ? payload : null;
        state.detectedCountry = null;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(updateUserSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.status = "succeeded";
        Object.assign(state, action.payload);
        saveToLocalStorage(state);
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(userProfileInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userProfileInfo.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userProfile = action.payload.data;
      })
      .addCase(userProfileInfo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

/* =========================
   EXPORTS
 ========================= */
export const {
  setTheme,
  setLanguage,
  setNotifications,
  setShipToCountry,
  setSettingsSlice,
  setSettingsHydrated,
  setCountrySource,
  hydrateFromStorage,
} = userSettingsSlice.actions;

export { loadInitialState, normalizeCountryCode, normalizeCurrencyCode };

export default userSettingsSlice.reducer;
